import { useState } from "react";
import { Link } from "react-router-dom";
import { Send, MailCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const L = {
  it: {
    title: "Resta aggiornato",
    text: "Nuovi articoli e guide sul commercio internazionale, direttamente nella tua email. Una email ogni tanto, mai spam.",
    name: "Nome (opzionale)",
    namePh: "Il tuo nome",
    email: "Email",
    emailPh: "nome@azienda.com",
    cta: "Iscriviti",
    sending: "Iscrizione…",
    ok: "Fatto! Ti abbiamo inviato un'email con il link di conferma.",
    already: "Sei già iscritto alla newsletter. Grazie!",
    err: "Si è verificato un errore. Riprova tra poco.",
    errEmail: "Inserisci un indirizzo email valido.",
    privacy: "Puoi cancellarti con un click in qualsiasi momento."
  },
  en: {
    title: "Stay updated",
    text: "New articles and guides on international trade, straight to your inbox. An email once in a while, never spam.",
    name: "Name (optional)",
    namePh: "Your name",
    email: "Email",
    emailPh: "name@company.com",
    cta: "Subscribe",
    sending: "Subscribing…",
    ok: "Done! We sent you an email with the confirmation link.",
    already: "You are already subscribed. Thank you!",
    err: "Something went wrong. Please try again shortly.",
    errEmail: "Please enter a valid email address.",
    privacy: "You can unsubscribe with one click at any time."
  }
};

export default function NewsletterForm() {
  const { lang } = useLang();
  const a = L[lang];
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError(a.errEmail);
      return;
    }
    setError("");
    setState("busy");
    try {
      const { data } = await api.post("/newsletter/subscribe", {
        name: name.trim() || null,
        email: email.trim(),
        lang,
      });
      setState(data?.already ? "already" : "ok");
    } catch (err) {
      setError(formatApiError(err) || a.err);
      setState("idle");
    }
  };

  if (state === "ok" || state === "already") {
    return (
      <div data-testid="newsletter-success" className="border border-gold/30 bg-gold/[0.07] p-6">
        <p className="flex items-center gap-3 font-serif text-lg text-gold-bright">
          <MailCheck className="h-5 w-5 shrink-0" /> {state === "ok" ? a.ok : a.already}
        </p>
      </div>
    );
  }

  const inputCls =
    "w-full border border-ivory/20 bg-white/5 px-4 py-3 text-sm text-ivory placeholder:text-ivory/30 transition-colors focus:border-gold/60 focus:outline-none";
  const labelCls = "mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-ivory/50";

  return (
    <form onSubmit={submit} data-testid="newsletter-form" className="border border-gold/20 bg-white/[0.03] p-6">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{a.title}</p>
      <p className="mt-3 text-sm leading-relaxed text-ivory/60">{a.text}</p>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="nl-name" className={labelCls}>{a.name}</label>
          <input
            id="nl-name"
            data-testid="newsletter-name-input"
            className={inputCls}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={a.namePh}
          />
        </div>
        <div>
          <label htmlFor="nl-email" className={labelCls}>{a.email} *</label>
          <input
            id="nl-email"
            data-testid="newsletter-email-input"
            type="email"
            className={inputCls}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={a.emailPh}
            required
          />
        </div>
      </div>
      {error && <p className="mt-3 text-xs text-red-300" data-testid="newsletter-error">{error}</p>}
      <button
        type="submit"
        data-testid="newsletter-submit-button"
        disabled={state === "busy"}
        className="mt-5 inline-flex items-center gap-2 bg-gold px-6 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:bg-gold-bright disabled:pointer-events-none disabled:opacity-50"
      >
        <Send className="h-3.5 w-3.5" /> {state === "busy" ? a.sending : a.cta}
      </button>
      <p className="mt-4 text-[11px] leading-relaxed text-ivory/35">{a.privacy}</p>
      <Link to="/newsletter/archivio" data-testid="newsletter-archive-link" className="mt-3 inline-block text-[11px] uppercase tracking-[0.15em] text-gold/80 transition-colors hover:text-gold-bright">
        {lang === "it" ? "Leggi gli invii passati" : "Read past issues"}
      </Link>
    </form>
  );
}
